import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import {
  Address,
  Currency,
  CustomerModel,
  GetCustomersData,
  GetCustomerSettingsResponseModel,
  GetFeesData,
  IndiaBusinessRegistrationType,
  IndianBankAccount,
  NfcCardModel,
  PatchUserData,
  PayoutBankDetails,
  SendOtpData,
  UserModel,
  VerifyOtpData,
  type GetCustomerByIdData,
} from '../../../api/autogenerated';
import {
  buildWalletBalancesByCurrency,
  getWalletBalanceForCurrency,
} from '../../../shared/utils/account-balance.util';
import { buildCurrencyOptionsFromVbanDetailsList } from '../../../shared/utils/currency-options.util';
import { mapVbanDetailsListToPaymentMethodsByCurrency } from '../../../shared/utils/vban-details.mapper';
import {
  IndianAddress,
  Address as TAddress,
  USAddress,
} from '../../../types/Address';
import { BusinessRegistrationType } from '../../../types/BusinessRegistrationType';
import { PaymentMethodDetails } from '../../../types/PaymentDetails';
import { Profile } from '../../../types/Profile';
import { NFCCardRequestObject } from '../../../types/requests/NFCCardRequestObject';
import { PayoutFeesResponse } from '../../../types/responses/PayoutFeesResponse';
import { UserProfile } from '../../../types/UserProfile';
import { SharedProfileData } from '../../data/shared-profile-data';
import { IProfileManagementService } from '../IProfileManagementService';
import { mockPayoutFeesList, mockProfile } from '../mock/mock-data';
import { OpenApiService } from '../open-api.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileManagementService implements IProfileManagementService {
  private profile: Profile = { ...mockProfile };

  private payoutFeesList: PayoutFeesResponse[] = mockPayoutFeesList;

  options = {
    headers: {
      'X-Organization-ID': environment.organization_id,
      customer_id: '',
    },
  };

  constructor(
    private sharedProfileData: SharedProfileData,
    private openApiService: OpenApiService,
  ) {}

  async getUserId(): Promise<string> {
    let dataFromResponse = await this.openApiService.getOrCreateUser();
    if (dataFromResponse.data.id != undefined) {
      return dataFromResponse.data.id;
    }
    return '';
  }

  async getProfile(forceUpdate?: boolean): Promise<Profile> {
    console.log('Fetching profile');
    const cachedProfile = this.sharedProfileData.getUserProfile();
    if (!forceUpdate && cachedProfile != null) {
      const userResponse = await this.openApiService.getOrCreateUser();
      const contextCustomerId = userResponse.data?.current_context_customer_id;
      if (contextCustomerId && cachedProfile.businessId === contextCustomerId) {
        return cachedProfile;
      }
    }

    let dataFromResponse = await this.openApiService.getOrCreateUser();
    if (dataFromResponse.data.current_context_customer_id == undefined) {
      return {} as Profile;
    }

    const customerId = dataFromResponse.data.current_context_customer_id;

    let requestObj: GetCustomerByIdData['path'] = {
      id: customerId,
    };

    const [
      dataFromCustomerCallResponse,
      settingsResponse,
      bankAccountsResponse,
    ] = await Promise.all([
      this.openApiService.getCustomerById(requestObj),
      this.openApiService.getCustomerSettings(customerId),
      this.openApiService.getBankAccounts(customerId),
    ]);

    const partnerVbanExists = await this.detectPartnerVbanExists(customerId);

    if (dataFromCustomerCallResponse.data === undefined) {
      throw new Error('Api response error');
    }

    let profile: Profile = this.convertProfileFromApiToFrontend(
      dataFromCustomerCallResponse.data,
      settingsResponse.data,
    );

    const bankAccounts =
      bankAccountsResponse.data?.data?.indian_bank_account_list;
    if (bankAccounts?.length) {
      profile.paymentMethodDetails =
        this.mapIndianBankAccountsToPaymentDetails(bankAccounts);
      const defaultPaymentMethod = profile.paymentMethodDetails.find(
        (method) => method.isDefault === true,
      );
      if (defaultPaymentMethod) {
        profile.accountNumber = defaultPaymentMethod.accountNumber;
        profile.ifscCode = defaultPaymentMethod.ifscCode;
      }
    }

    if (partnerVbanExists) {
      profile.partnerVbanExists = true;
    }
    //Handle the phone number temporally
    if (
      profile.phoneNumbers == undefined ||
      profile.phoneNumbers?.length == 0
    ) {
      if (dataFromResponse.data?.phone_number != undefined) {
        profile.phoneNumbers = [dataFromResponse.data.phone_number];
      }
    }

    this.sharedProfileData.setUserProfile(profile);
    if (profile.profilePicture != undefined && profile.profilePicture != '') {
      try {
        profile.profileImage = await this.getLogoImage(profile.profilePicture);
      } catch (e) {
        console.log('Error fetching the profile image', profile);
      }
    }
    this.sharedProfileData.setUserProfile(profile);
    return profile;
    // return Promise.resolve(this.profile);
  }

  async removeCustomerProfilePicture(profile: Profile): Promise<Profile> {
    let patchCustomerResponse = await this.openApiService.patchCustomer({
      id: profile.businessId,
      operation: 'REMOVE_COMPANY_LOGO',
    });
    return this.convertProfileFromApiToFrontend(patchCustomerResponse.data, {});
  }

  async updateProfile(profile: Partial<Profile>): Promise<Profile> {
    console.log('Updating profile', profile);
    let requestBody: PatchUserData['body'] = {
      name: profile.name,
    };
    let responseFromPatchUser =
      await this.openApiService.patchUser(requestBody);

    this.convertUserModelToUserProfile(responseFromPatchUser.data);

    // todo: Need to check that this function should return UserProfile object
    // this.profile = { ...this.profile, ...profile };
    return Promise.resolve(this.profile);
  }

  async updateUserProfile(
    userProfile: Partial<UserProfile>,
  ): Promise<UserProfile> {
    console.log('Updating user profile', userProfile);
    let requestBody: PatchUserData['body'] = {
      name: userProfile.name,
    };
    let responseFromPatchUser =
      await this.openApiService.patchUser(requestBody);

    return this.convertUserModelToUserProfile(responseFromPatchUser.data);
  }

  async addPaymentMethod(
    paymentMethod: PaymentMethodDetails,
  ): Promise<Profile> {
    console.log('Adding payment method', paymentMethod);
    this.profile.paymentMethodDetails.push(paymentMethod);
    return Promise.resolve(this.profile);
  }

  async removePaymentMethod(accountNumber: string): Promise<Profile> {
    console.log('Removing payment method with account number', accountNumber);
    this.profile.paymentMethodDetails =
      this.profile.paymentMethodDetails.filter(
        (method) => method.accountNumber !== accountNumber,
      );
    return Promise.resolve(this.profile);
  }

  async getPayoutFees(
    businessId: string,
    sellVolume: number,
    sellCurrency: string,
  ): Promise<PayoutFeesResponse> {
    const normalizedSellCurrency = sellCurrency?.trim().toUpperCase() || 'USD';
    let params: GetFeesData['query'] = {
      sell_amount: sellVolume,
      sell_currency: normalizedSellCurrency as Currency,
      buy_currency: 'INR',
    };
    let responseFromApi = await this.openApiService.getFees(businessId, params);
    return {
      grossAmountInSourceCurrency: responseFromApi.data.gross_amount,
      netAmountInSourceCurrency: responseFromApi.data.sell_amount,
      netAmountInDestinationCurrency: responseFromApi.data.buy_amount,
      destinationCurrency: responseFromApi.data.buy_currency,
      estimatedPayoutDate: '',
      fixedPayoutFees: responseFromApi.data.fixed_fee
        ? responseFromApi.data.fixed_fee
        : 0,
      payoutFeesIsFixed: !(
        responseFromApi.data.variable_fee_percentage &&
        responseFromApi.data.variable_fee_percentage > 0
      ),
      payoutFeesPlanId: '',
      sourceCurrency: responseFromApi.data.sell_currency,
      variablePayoutFeesMinimum: responseFromApi.data.minimum_amount
        ? responseFromApi.data.minimum_amount
        : 0,
      variablePayoutFeesPercentage: responseFromApi.data.variable_fee_percentage
        ? responseFromApi.data.variable_fee_percentage
        : 0,
      conversionAgainstOneUnit: responseFromApi.data.user_conversion_rate
        ? responseFromApi.data.user_conversion_rate
        : 0,
      variablePayoutFees: responseFromApi.data.variable_fee
        ? responseFromApi.data.variable_fee
        : 0,
      totalPayoutFees: responseFromApi.data.total_fee
        ? responseFromApi.data.total_fee
        : 0,
    };
  }

  async postPayoutsPreview(
    businessId: string,
    invoiceId: string,
    reconciliationAmount: number,
  ): Promise<string | null> {
    let responseFromApi = await this.openApiService.postPayoutsPreview(
      businessId,
      {
        invoice_id: invoiceId,
        reconciliation_amount: reconciliationAmount,
      },
    );
    if (
      responseFromApi.success &&
      responseFromApi.data?.expected_settlement_date
    ) {
      return responseFromApi.data.expected_settlement_date;
    }
    return null;
  }

  //Fetch conversion rate for rate card in header
  async getConversionRate(
    sellCurrency: string,
    buyCurrency: string,
  ): Promise<{
    userConversionRate: number;
    midMarketRate: number;
  }> {
    const profile = await this.getProfile(false);
    if (!profile?.businessId) {
      throw new Error('No businessId available in profile');
    }

    console.log('Customer Id: ', profile.businessId);
    let params: GetFeesData['query'] = {
      sell_amount: 100,
      sell_currency: sellCurrency as Currency,
      buy_currency: buyCurrency as Currency,
    };

    let responseFromApi = await this.openApiService.getFees(
      profile.businessId,
      params,
    );

    if (!responseFromApi.success) {
      throw new Error(
        responseFromApi.error || 'Error fetching conversion rate',
      );
    }

    console.log('Fees api response: ', responseFromApi.data);
    return {
      userConversionRate: responseFromApi.data?.user_conversion_rate ?? 0,
      midMarketRate: responseFromApi.data?.mid_market_rate ?? 0,
    };
  }

  async getAllActiveProfiles(): Promise<Profile[]> {
    let customerRequestContent: GetCustomersData['query'] = {
      status: 'ACTIVE',
      customer_type: 'BUSINESS',
      mine: true,
    };
    let profiles: Profile[] = [];

    let responseFromGetCustomers = await this.openApiService.getCustomers(
      customerRequestContent,
    );
    const mappedCustomerIdToImage = await this.mapLogoImageToCustomer(
      responseFromGetCustomers.data,
    );
    responseFromGetCustomers.data.forEach((customer) => {
      let profile = this.convertProfileFromApiToFrontend(customer);
      if (customer?.id)
        profile.profileImage = mappedCustomerIdToImage[customer.id];
      profiles.push(profile);
    });
    return profiles;
  }

  async getAllProfiles(): Promise<Profile[]> {
    let customerRequestContent: GetCustomersData['query'] = {
      customer_type: 'BUSINESS',
      mine: true,
    };

    let profiles: Profile[] = [];

    let responseFromGetCustomers = await this.openApiService.getCustomers(
      customerRequestContent,
    );
    responseFromGetCustomers.data.forEach((customer) => {
      let profile = this.convertProfileFromApiToFrontend(customer);
      profiles.push(profile);
    });
    return profiles;
  }

  async getLogoImage(fileId: string): Promise<Blob> {
    let response = null;
    const user: Profile = this.sharedProfileData.getUserProfile();
    response = await this.openApiService.getFileContentById(
      user.businessId,
      fileId,
      'COMPANY_LOGO',
    );
    return response.data as Blob;
  }

  async setProfile(profile: Profile): Promise<Profile> {
    let patchCustomerResponse = await this.openApiService.patchCustomer({
      id: profile.businessId,
      operation: 'UPDATEASDEFAULT',
    });
    return this.convertProfileFromApiToFrontend(patchCustomerResponse.data);
  }

  private getAddress(address: Address): TAddress {
    let addressAss = {} as TAddress;
    addressAss.street = address.address_line1;
    addressAss.city = address.city;
    addressAss.state = address.state;
    addressAss.country = address.country_code;
    if (addressAss.country === 'IN') {
      addressAss.specificDetails = {
        stateName: address.state,
        pinCode: address.pin_code,
      } as IndianAddress;
    } else if (addressAss.country === 'US') {
      addressAss.specificDetails = {
        stateCode: address.state,
        zipCode: address.pin_code,
      } as USAddress;
    }
    return addressAss;
  }

  private convertUserModelToUserProfile(user: UserModel): UserProfile {
    return {
      userId: user.id ?? '',
      name: user.name ?? '',
      email: user.email,
      phoneNumber: user.phone_number ?? '',
      affiliateManager: user.affiliate_manager ?? false,
      currentContextCustomerId: user.current_context_customer_id,
    };
  }

  private convertProfileFromApiToFrontend(
    customer: CustomerModel,
    settings: GetCustomerSettingsResponseModel = {} as GetCustomerSettingsResponseModel,
  ): Profile {
    const walletBalancesByCurrency = buildWalletBalancesByCurrency(
      customer.wallet_balance_list,
    );
    const defaultPayoutBankDetails = this.getDefaultPayoutBankDetails(customer);

    let profile: Profile = {
      address: customer.address
        ? this.getAddress(customer.address)
        : ({} as TAddress),
      businessId: customer.id ?? '',
      businessRegistrationType:
        this.mapperForApiToFrontendBusinessActivity()[
          customer.business_registration_type as IndiaBusinessRegistrationType
        ],
      status: customer.status?.toString().toLowerCase(),
      connectedAccounts: '',
      createdDate: customer.verified_at,
      emails: customer.emails ? customer.emails : [],
      name: customer.name ?? '',
      paymentMethodDetails: this.getPaymentDetails(customer),
      vbanBankDetailsByCurrency: mapVbanDetailsListToPaymentMethodsByCurrency(
        customer.vban_details_list,
      ),
      vbanCurrencyOptions: buildCurrencyOptionsFromVbanDetailsList(
        customer.vban_details_list,
      ),
      phoneNumbers: customer.phone_numbers ? customer.phone_numbers : [],
      profilePicture: customer.profile_picture,
      accountNumber: defaultPayoutBankDetails?.account_number,
      ifscCode: defaultPayoutBankDetails?.ifsc_code,
      autoWithdrawalEnabled: settings.auto_withdrawal === 'ENABLE',
      autoWithdrawalConsent: settings.auto_withdrawal_consent === true,
      multiCurrencyStatus: this.getMultiCurrencyStatus(settings),
      multiCurrencyActivated: this.isMultiCurrencyStatusActivated(settings),
      vendorVbanStatus: this.getVendorVbanStatus(settings),
      walletBalancesByCurrency,
      usdWalletBalance: getWalletBalanceForCurrency(
        walletBalancesByCurrency,
        'USD',
      ),
    };
    return profile;
  }

  private getVendorVbanStatus(
    settings: GetCustomerSettingsResponseModel,
  ): string {
    const settingsWithLegacyField =
      settings as GetCustomerSettingsResponseModel & {
        vendor_vban_status?: string;
      };
    return (
      settings.vendor_vban_status ??
      settingsWithLegacyField.vendor_vban_status ??
      ''
    ).toUpperCase();
  }

  private getMultiCurrencyStatus(
    settings: GetCustomerSettingsResponseModel,
  ): string {
    const settingsWithLegacyField =
      settings as GetCustomerSettingsResponseModel & {
        multi_currency_status?: string;
      };
    return (
      settings.multi_currency_status ??
      settingsWithLegacyField.multi_currency_status ??
      ''
    ).toUpperCase();
  }

  private isMultiCurrencyStatusActivated(
    settings: GetCustomerSettingsResponseModel,
  ): boolean {
    return this.getMultiCurrencyStatus(settings) === 'ACTIVATED';
  }

  private getDefaultPayoutBankDetails(
    customer: CustomerModel,
  ): PayoutBankDetails | undefined {
    const payoutDetailsList = customer.payout_bank_details;
    if (!payoutDetailsList?.length) {
      return undefined;
    }

    return payoutDetailsList.find((details) => details.is_default === true);
  }

  async setDefaultBankAccount(bankAccountId: string): Promise<Profile> {
    const profile = await this.getProfile(false);
    if (!profile.businessId) {
      throw new Error('No business profile available');
    }
    if (!bankAccountId?.trim()) {
      throw new Error('Bank account ID is required');
    }

    const response = await this.openApiService.patchBankAccount(
      profile.businessId,
      bankAccountId,
      { is_default: true },
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to set default bank account');
    }

    return this.getProfile(true);
  }

  /**
   * Fetches bank accounts directly (GET /api/v1/bank_accounts) without touching the
   * cached profile — for callers (the withdrawal modal) that want the latest account
   * list on every open without paying for a full customer + settings refetch.
   */
  async getBankAccounts(customerId: string): Promise<PaymentMethodDetails[]> {
    const response = await this.openApiService.getBankAccounts(customerId);
    const bankAccounts = response.data?.data?.indian_bank_account_list;
    if (!bankAccounts?.length) {
      return [];
    }
    return this.mapIndianBankAccountsToPaymentDetails(bankAccounts);
  }

  private mapIndianBankAccountsToPaymentDetails(
    accounts: IndianBankAccount[],
  ): PaymentMethodDetails[] {
    return accounts.map((account) => ({
      id: account.id,
      paymentMethodType: account.account_type ?? '',
      accountName: account.account_name ?? '',
      accountNumber: account.account_no ?? '',
      achRouting: '',
      accountType: account.account_type ?? '',
      bankName: account.bank_name ?? '',
      bankAddress: account.bank_branch ?? '',
      ifscCode: account.ifsc_code ?? '',
      isDefault: account.is_default === true,
    }));
  }

  private getPaymentDetails(customer: CustomerModel): PaymentMethodDetails[] {
    const payoutDetailsList = customer.payout_bank_details;

    if (!payoutDetailsList?.length) {
      return [];
    }

    return payoutDetailsList.map((payoutDetails) => ({
      paymentMethodType: '',
      accountName: payoutDetails.account_name ?? '',
      accountNumber: payoutDetails.account_number ?? '',
      achRouting: '',
      accountType: '',
      bankName: payoutDetails.bank_name ?? '',
      bankAddress: payoutDetails.bank_address ?? '',
      ifscCode: payoutDetails.ifsc_code ?? '',
      isDefault: payoutDetails.is_default === true,
    }));
  }

  /**
   * Load partners owned by this customer; if at least one has partnerVbanExists, the vendor
   * is treated as a legacy partner-VBAN user for profile / UI mode.
   */
  private async detectPartnerVbanExists(customerId: string): Promise<boolean> {
    try {
      const partnersResponse = await this.openApiService.getPartners({
        owning_customer_id: customerId,
      });

      if (!partnersResponse.success || !partnersResponse.data?.length) {
        return false;
      }

      return partnersResponse.data.some((partner) => {
        const record = partner as typeof partner & {
          partner_vban_exists?: boolean;
        };
        return (
          record.partnerVbanExists === true ||
          record.partner_vban_exists === true
        );
      });
    } catch {
      return false;
    }
  }

  private mapperForApiToFrontendBusinessActivity(): {
    [key in IndiaBusinessRegistrationType]: BusinessRegistrationType;
  } {
    return {
      PRIVATE_LIMITED_COMPANY: BusinessRegistrationType.PrivateLimitedCompany,
      PUBLIC_LIMITED_COMPANY: BusinessRegistrationType.PublicLimitedCompany,
      PARTNERSHIPS: BusinessRegistrationType.Partnerships,
      LLP_REGISTRATION: BusinessRegistrationType.LlpRegistration,
      ONE_PERSON_COMPANY: BusinessRegistrationType.OnePersonCompany,
      SOLE_PROPRIETORSHIP: BusinessRegistrationType.SoleProprietorship,
      SECTION_8_COMPANY: BusinessRegistrationType.Section8Company,
    };
  }

  async mapLogoImageToCustomer(
    customers: CustomerModel[],
  ): Promise<{ [key: string]: Blob }> {
    let mapingCustomerIdToImage: { [key: string]: Blob } = {};

    const promises = customers
      .filter((customer) => customer?.id && customer?.profile_picture)
      .map(async (customer) => {
        try {
          const image: Blob = await this.getLogoImage(
            customer.profile_picture || '',
          );
          return { id: customer.id!, image, success: true };
        } catch (error) {
          console.error(
            `Failed to fetch logo for customer ${customer.id}:`,
            error,
          );
          return { id: customer.id!, success: false };
        }
      });

    const results = await Promise.all(promises);

    results.forEach((result) => {
      if (result.success && 'image' in result && result.image instanceof Blob) {
        mapingCustomerIdToImage[result.id] = result.image;
      }
    });
    return mapingCustomerIdToImage;
  }

  async getNfcCardDetails(
    userId: string,
  ): Promise<{ success: boolean; data: NfcCardModel; error: string }> {
    return this.openApiService.getNfcCardDetails(userId);
  }

  async createOrUpdateNfcCard(
    userId: string,
    body: NFCCardRequestObject,
  ): Promise<{ success: boolean; data: NfcCardModel; error: string }> {
    return this.openApiService.createOrUpdateNfcCard(userId, body);
  }

  async sendOTPToUserPhoneNumber(phoneNumber: string): Promise<boolean> {
    console.log('Sending OTP to phone number', phoneNumber);

    let requestBody: SendOtpData['body'] = {
      phone_number: phoneNumber,
      verification_operation: 'MOBILE_NUMBER_VERIFICATION',
    };

    let responseFromApi = await this.openApiService.sendOtp(requestBody);
    if (!responseFromApi.success) {
      return false;
    }
    return responseFromApi.data.status !== undefined;
  }

  async verifyOTP(phoneNumber: string, otp: string): Promise<boolean> {
    const requestBody: VerifyOtpData['body'] = {
      phone_number: phoneNumber,
      otp: otp,
      verification_operation: 'MOBILE_NUMBER_VERIFICATION',
    };

    let responseFromApi = await this.openApiService.verifyOtp(requestBody);

    if (!responseFromApi.success) {
      return false;
    }

    if (responseFromApi.data.status === 'approved') {
      const requestBody: PatchUserData['body'] = {
        phone_number: phoneNumber,
      };
      await this.openApiService.patchUser(requestBody);
    }

    return responseFromApi.data.status === 'approved';
  }

  async getUserProfile(): Promise<UserProfile> {
    let dataFromResponse = await this.openApiService.getOrCreateUser();
    if (!dataFromResponse.success) {
      return {} as UserProfile;
    }
    return this.convertUserModelToUserProfile(dataFromResponse.data);
  }

  async isContextCustomerActive(customerId: string): Promise<boolean> {
    const response = await this.openApiService.getCustomerById({
      id: customerId,
    });
    if (!response.success || !response.data?.status) {
      return false;
    }
    return response.data.status === 'ACTIVE';
  }

  async enableAutoWithdrawal(
    customerProfile: Partial<Profile>,
  ): Promise<boolean> {
    console.log('Enabling auto withdrawal for profile', customerProfile);
    if (!customerProfile.businessId) {
      return false;
    }

    const patchResponse = await this.openApiService.patchCustomerSettings(
      customerProfile.businessId,
      {
        auto_withdrawal: 'ENABLE',
        auto_withdrawal_consent: true,
      },
    );

    if (patchResponse.success) {
      const profile = this.sharedProfileData.getUserProfile();
      if (profile) {
        profile.autoWithdrawalEnabled =
          patchResponse.data.auto_withdrawal === 'ENABLE';
        profile.autoWithdrawalConsent = true;
        this.sharedProfileData.setUserProfile(profile);
      }
    }

    return patchResponse.success;
  }

  async setAutoWithdrawalConsent(
    customerProfile: Partial<Profile>,
  ): Promise<boolean> {
    if (!customerProfile.businessId) {
      return false;
    }

    const patchResponse = await this.openApiService.patchCustomerSettings(
      customerProfile.businessId,
      {
        auto_withdrawal_consent: true,
      },
    );

    if (patchResponse.success) {
      const profile = this.sharedProfileData.getUserProfile();
      if (profile) {
        profile.autoWithdrawalConsent = true;
        this.sharedProfileData.setUserProfile(profile);
      }
    }

    return patchResponse.success;
  }

  async disableAutoWithdrawal(
    customerProfile: Partial<Profile>,
  ): Promise<boolean> {
    console.log('Disabling auto withdrawal for profile', customerProfile);
    if (!customerProfile.businessId) {
      return false;
    }
    const patchResponse = await this.openApiService.patchCustomerSettings(
      customerProfile.businessId,
      {
        auto_withdrawal: 'DISABLE',
      },
    );

    if (patchResponse.success) {
      // Update the profile with latest settings
      const profile = this.sharedProfileData.getUserProfile();
      if (profile) {
        profile.autoWithdrawalEnabled =
          patchResponse.data.auto_withdrawal === 'ENABLE';
        this.sharedProfileData.setUserProfile(profile);
      }
    }

    return patchResponse.success;
  }
}
